import { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import Typography from "@material-ui/core/Typography";

// Form
import SmarthopForm from "@smarthop/form/SmarthopForm";
import { yardForm } from "@smarthop/form/configs/yardForm";

// Service
import { signInWithToken } from "app/services/LoginService";

// Utils
import { showSnackbar } from "app/main/utils/snackbarUtil";
import { incrementDataRevision } from "app/store/tools/revisionSlice";
import { setUserData } from "app/store/auth/userSlice";

function YardView(props) {
	const dispatch = useDispatch();
	const snackbar = useSnackbar();

	const hasSubaccounts = useSelector(({ auth }) => auth.account.subAccount?.hasSubaccounts);
	const user = useSelector(({ auth }) => auth.user);

	const native = window?.ReactNativeWebView ?? false;

	const dataIds = props?.dataIds;
	const onDone = props?.onDone;

	const mode = dataIds?.mode;
	const yardId = dataIds?.id;
	const historyData = dataIds?.historyData;
	const fromCalendar = dataIds?.fromCalendar;
	const carrierOption = hasSubaccounts && dataIds?.carrierOption;

	const isCreate = mode === "CREATE";
	const carrierId = carrierOption ? null : dataIds?.carrierId;

	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [processing, setProcessing] = useState(false);
	const [error, setError] = useState(null);

	useEffect(() => {
		props?.setTitle?.(historyData ? "View Yard" : isCreate ? "Add New Yard" : "Edit Yard");
		props?.setSize?.("max-w-xl");
		// eslint-disable-next-line
	}, []);

	const form = useMemo(() => yardForm({ user }), [user]);

	if (loading) {
		return (
			<div className="flex w-full h-512 flex-col items-center justify-center">
				<Typography color="primary" className="text-13 font-light mx-20 py-6 md:pb-48">
					Loading...
				</Typography>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex w-full h-512 flex-col items-center justify-center">
				<Typography className="w-full text-center" color="error">
					{error}
				</Typography>
			</div>
		);
	}

	return (
		<div className="mt-15">
			<SmarthopForm
				form={form}
				dataIds={{ ...dataIds, carrierId }}
				data={data}
				mode={historyData ? "COMPARE" : mode}
				onDone={() => {
					if (isCreate) {
						signInWithToken().then((userData) => {
							if (userData) dispatch(setUserData(userData, { skip_settings_set: true }));
						});
					}

					showSnackbar(snackbar, "Successfully Saved Information", "success");

					dispatch(incrementDataRevision({ event: "profileRevision" }));
					dispatch(incrementDataRevision({ event: "yardRevision" }));
					dispatch(incrementDataRevision({ event: "dataRevision" }));
					if (fromCalendar) dispatch(incrementDataRevision({ event: "dashboardRevision" }));
					if (native) window?.ReactNativeWebView?.postMessage(JSON.stringify({ type: "REFRESH_YARD_LIST" }));
					onDone?.();
				}}
				onProgress={(progress) => {
					setProcessing(progress);
				}}
				onCustom={(event, formData, { setValue, getValues }) => {
					// Handle address autocomplete selection to populate related fields
					if (event?.type === "FIELD_CHANGE" && event?.fieldKey === "address__view") {
						const addressValue = getValues("address__view");
						const selectedOption = addressValue?.options?.[0];

						if (selectedOption?.metadata) {
							const metadata = selectedOption.metadata;

							// Populate related fields from the selected address
							if (metadata.coords?.lat) {
								setValue("lat", parseFloat(metadata.coords.lat));
							}
							if (metadata.coords?.long) {
								setValue("lng", parseFloat(metadata.coords.long));
							}
							if (metadata.city) {
								setValue("city", metadata.city);
							}
							if (metadata.state) {
								setValue("state", metadata.state);
							}
							if (metadata.zip) {
								setValue("zipCode", metadata.zip);
							}
						}
					}

					// Handle search address autocomplete selection to populate search location fields
					if (event?.type === "FIELD_CHANGE" && event?.fieldKey === "search_address__view") {
						const searchAddressValue = getValues("search_address__view");
						const selectedOption = searchAddressValue?.options?.[0];

						if (selectedOption?.metadata) {
							const metadata = selectedOption.metadata;

							// Populate search location fields from the selected address
							if (metadata.coords?.lat) {
								setValue("search_lat", parseFloat(metadata.coords.lat));
							}
							if (metadata.coords?.long) {
								setValue("search_lng", parseFloat(metadata.coords.long));
							}
							if (metadata.city) {
								setValue("search_city", metadata.city);
							}
							if (metadata.state) {
								setValue("search_state", metadata.state);
							}
						}
					}
				}}
			/>
		</div>
	);
}

export default YardView;
